import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import client, { auth, API } from "../lib/api";
import { Download, LogOut, RefreshCw, Search, Home as HomeIcon, LayoutDashboard } from "lucide-react";

const STATUSES = ["New", "Contacted", "Qualified", "Offer Made", "Dead"];
const SCORE_CLASS = { Hot: "badge-hot", Warm: "badge-warm", Cold: "badge-cold" };

export default function AdminDashboard() {
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!auth.isAuthed()) nav("/admin/login");
  }, [nav]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, a] = await Promise.all([
        client.get("/admin/leads", { params: { status: status || undefined, q: q || undefined } }),
        client.get("/admin/analytics"),
      ]);
      setLeads(l.data.leads);
      setAnalytics(a.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        auth.logout();
        nav("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  }, [status, q, nav]);

  useEffect(() => { load(); }, [load]);

  const downloadCSV = async () => {
    const token = auth.getToken();
    const res = await fetch(`${API}/admin/leads.csv`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "faircashdeals-leads.csv";
    a.click();
  };

  const counts = analytics?.by_status || {};

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[240px_1fr] bg-offwhite" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="bg-navy text-white lg:min-h-screen p-6 flex flex-col">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <img src="/logo-aspire-icon-256.png" alt="Aspire Collective Partners LLC" className="h-10 w-10 object-contain bg-white rounded-md p-1" />
          <span className="font-display text-lg">Aspire Collective Partners LLC Admin</span>
        </Link>
        <nav className="space-y-1 text-sm">
          <div className="flex items-center gap-2 py-2 px-3 rounded-md bg-white/10"><LayoutDashboard size={16} /> Dashboard</div>
          <Link to="/" className="flex items-center gap-2 py-2 px-3 rounded-md text-white/80 hover:bg-white/5"><HomeIcon size={16} /> View site</Link>
        </nav>
        <div className="mt-auto pt-6 border-t border-white/10 text-sm">
          <div className="text-white/70 mb-2 truncate">{auth.email()}</div>
          <button onClick={() => { auth.logout(); nav("/admin/login"); }} className="flex items-center gap-2 text-money font-semibold" data-testid="admin-logout-btn">
            <LogOut size={14} /> Log out
          </button>
        </div>
      </aside>

      <main className="p-6 sm:p-10">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
          <div>
            <h1 className="font-display text-3xl text-navy">Lead Inbox</h1>
            <p className="text-muted text-sm mt-1">All incoming cash-offer requests, freshest first.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="h-10 px-3 rounded-md border-2 border-navy text-navy font-semibold flex items-center gap-2" data-testid="refresh-btn"><RefreshCw size={14} /> Refresh</button>
            <button onClick={downloadCSV} className="h-10 px-3 rounded-md bg-money text-white font-semibold flex items-center gap-2" data-testid="export-csv-btn"><Download size={14} /> Export CSV</button>
          </div>
        </div>

        {/* Analytics tiles */}
        <div className="grid gap-4 grid-cols-2 md:grid-cols-6 mb-8">
          <Tile label="Total leads" value={analytics?.total ?? "—"} accent testId="tile-total" />
          {STATUSES.map((s) => (
            <Tile key={s} label={s} value={counts[s] ?? "—"} testId={`tile-status-${s.toLowerCase().replace(/\s+/g,'-')}`} />
          ))}
        </div>

        <div className="grid gap-4 md:grid-cols-3 mb-8">
          <div className="bg-white p-5 rounded-md border border-[var(--border-soft)]">
            <div className="text-sm font-semibold text-navy-800 mb-3">By AI Score</div>
            <div className="space-y-2">
              {["Hot","Warm","Cold"].map((s) => (
                <div key={s} className="flex items-center justify-between text-sm">
                  <span className={`px-2 py-1 rounded-full text-xs font-bold ${SCORE_CLASS[s]}`}>{s}</span>
                  <span className="font-display text-xl text-navy">{analytics?.by_score?.[s] ?? 0}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white p-5 rounded-md border border-[var(--border-soft)] md:col-span-2">
            <div className="text-sm font-semibold text-navy-800 mb-3">Top situations</div>
            <div className="grid grid-cols-2 gap-2">
              {(analytics?.by_situation || []).slice(0,6).map((r) => (
                <div key={r.situation} className="flex items-center justify-between text-sm bg-offwhite p-2 rounded">
                  <span className="text-navy-800 truncate mr-2">{r.situation || "Unknown"}</span>
                  <span className="font-semibold text-navy">{r.count}</span>
                </div>
              ))}
              {(!analytics?.by_situation || analytics.by_situation.length === 0) && (
                <div className="text-sm text-muted">No data yet.</div>
              )}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-4">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-3 text-muted" />
            <input value={q} onChange={(e)=>setQ(e.target.value)} onKeyDown={(e)=>e.key==="Enter" && load()} placeholder="Search address, name, phone…" className="h-10 pl-9 pr-3 border-2 border-[var(--border-soft)] rounded-md bg-white w-72" data-testid="lead-search-input" />
          </div>
          <select value={status} onChange={(e)=>setStatus(e.target.value)} className="h-10 px-3 border-2 border-[var(--border-soft)] rounded-md bg-white" data-testid="status-filter-select">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-md border border-[var(--border-soft)] overflow-x-auto" data-testid="leads-table">
          <table className="w-full text-sm">
            <thead className="bg-offwhite text-navy-800 text-xs uppercase tracking-wide">
              <tr>
                <Th>Score</Th><Th>Name</Th><Th>Address</Th><Th>Situation</Th><Th>Status</Th><Th>Received</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={7} className="p-8 text-center text-muted">Loading…</td></tr>)}
              {!loading && leads.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-muted">No leads yet. Submit a test lead from the public site.</td></tr>
              )}
              {leads.map((l) => (
                <tr key={l.id} className="border-t border-[var(--border-soft)] hover:bg-offwhite" data-testid={`lead-row-${l.id}`}>
                  <Td><span className={`px-2 py-1 rounded-full text-xs font-bold ${SCORE_CLASS[l.ai_score] || "badge-cold"}`}>{l.ai_score || "—"}</span></Td>
                  <Td>{l.name || "—"}<div className="text-xs text-muted">{l.phone}</div></Td>
                  <Td>{l.address}<div className="text-xs text-muted">{[l.city,l.state,l.zip].filter(Boolean).join(", ")}</div></Td>
                  <Td>{l.situation || "—"}</Td>
                  <Td><StatusPill status={l.status} /></Td>
                  <Td>{new Date(l.created_at).toLocaleString()}</Td>
                  <Td>
                    <Link to={`/admin/leads/${l.id}`} className="text-money font-semibold" data-testid={`view-lead-${l.id}`}>View →</Link>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}

function Th({ children }) { return <th className="text-left p-3 whitespace-nowrap">{children}</th>; }
function Td({ children }) { return <td className="p-3 align-top">{children}</td>; }
function Tile({ label, value, accent, testId }) {
  return (
    <div className={`p-4 rounded-md border ${accent ? "bg-navy text-white border-navy" : "bg-white border-[var(--border-soft)]"}`} data-testid={testId}>
      <div className={`text-xs uppercase tracking-wide ${accent ? "text-white/70" : "text-muted"}`}>{label}</div>
      <div className={`font-display text-2xl mt-1 ${accent ? "text-white" : "text-navy"}`}>{value}</div>
    </div>
  );
}
function StatusPill({ status }) {
  const map = {
    "New": "bg-blue-100 text-blue-800",
    "Contacted": "bg-yellow-100 text-yellow-800",
    "Qualified": "bg-emerald-100 text-emerald-800",
    "Offer Made": "bg-money text-white",
    "Dead": "bg-gray-200 text-gray-700",
  };
  return <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${map[status] || "bg-gray-100"}`}>{status}</span>;
}
